import {
  CreditDecision,
  EBankStatementsRequiredFor,
  EDecision,
  EFinancingProgram,
  getEDecisionList,
  getEFinancingProgramList,
} from '@if/core/types'
import * as yup from '@if/core/yup'
import { useTranslation } from 'react-i18next'
import { EAfterNormEffectsEnum } from './constants'

export interface TotalDebtService {
  dsAmnt_A: number
  dsAmnt_B: number
  dsAmnt_C: number
  dsAmnt_D: number
  dsAmnt_E: number
  dsAmnt_F: number
  totalDebtRatioForIfinance: number
  totalDebtRatioBeforeNewLoan: number
  totalDebtRatio: number
  totalDebtRatioIncludingNewLoan: number
  sumDSAmnt: number
  SumDSAmntWihtoutF: number
}

export type ApplicantCreditDecision = CreditDecision & {
  score: number
  scoreMethod: string
  isNewToCredit: boolean
  canQualifyForGold: boolean
  decisionTraceId: string
  reason: string
  treeId: string
  hasError: boolean
  totalDebtService: TotalDebtService
  maxAdditionalAmount: number | null
}

export type NormCreditDecision = CreditDecision & {
  interestRateModifier: number
  afterNormEffects: EAfterNormEffectsEnum[]
}

export interface CreditDecisionOverrides {
  interestRate: number | null
  cashdownPercent: number | null
  bankStatementsRequiredFor: EBankStatementsRequiredFor | '' | null
  maxTermDuration: number | null
  maxPmtAmount: number | null
  existingCarLoanStatus: string | null
}

const typeExistingCarLoanStatus = yup.string().nullable().notRequired()

export const FinalCreditDecisionSchema = yup.object({
  cashdownPercent: yup.double().validPercent().max(100).positive(),
  maxTermDuration: yup.integer(),
  maxAmountFinanced: yup
    .double()
    .nullable()
    .when('decision', {
      is: (value: EDecision) => value && value === EDecision.Approved,
      then: (schema) => schema.positive().required(),
    }),
  financingProgramId: yup.mixed<EFinancingProgram>().oneOf(getEFinancingProgramList()),
  decision: yup.mixed<EDecision>().oneOf(getEDecisionList()).required(),
  refusalReasonCodes: yup.array(yup.string().required()),
  bankStatementsRequiredFor: yup.string().nullable(),
  noCoAplicant: yup.bool().default(true),
  existingCarLoanStatus: typeExistingCarLoanStatus,
  versionTag: yup.string(),
  maxPmtAmount: yup
    .double()
    .required()
    .validPercent()
    .when('decision', {
      is: (value: EDecision) => value && value !== EDecision.Refused,
      then: (schema) => schema.positive(),
    }), // Pas un pourcentage, mais seulement deux décimales acceptés
  interestRate: yup
    .double()
    .required()
    .validPercent()
    .max(100)
    .when('decision', {
      is: (value: EDecision) => value && value !== EDecision.Refused,
      then: (schema) => schema.positive(),
    }),
})

export type FinalCreditDecision = yup.InferType<typeof FinalCreditDecisionSchema>

export const WrapperFinalCreditDecisionSchema = (
  maxAllowedPayment: number,
  minAllowedPayment: number,
  minInterestRate: number,
  maxInterestRate: number,
) => {
  const { t } = useTranslation()

  return FinalCreditDecisionSchema.shape({
    maxPmtAmount: yup
      .double()
      .required()
      .validPercent()
      .when('decision', {
        is: (value: EDecision) => value && value !== EDecision.Refused,
        then: (schema) =>
          schema
            .required()
            .max(maxAllowedPayment, () => t('common.errors.overNorms_other', '', { count: maxAllowedPayment }))
            .min(minAllowedPayment, () => t('common.errors.underNorms_other', '', { count: minAllowedPayment })),
      }),
    interestRate: yup
      .double()
      .required()
      .validPercent()
      .max(100)
      .when('decision', {
        is: (value: EDecision) => value && value !== EDecision.Refused,
        then: (schema) => schema.required().positive().min(minInterestRate).max(maxInterestRate),
      }),
  })
}